import { CardStyleAsset } from "@1games-ru/region-shared/dist/types/bank/bank";
import { PropsWithChildren } from "react";
import { cn } from "@/core/helpers/cn";
import formatPrice from "@/core/helpers/formatPrice";
import { usePerspectiveCardHover } from "@/shared/hooks/usePerspectiveCardHover";
import { PayPassIcon } from "@/shared/icons/PayPassIcon";
import { BankCardBg } from "@/domains/bank/components/BankCard/components/BankCardBg";
import { MigLogo } from "@/domains/bank/components/BankCard/components/MigLogo";
import { SBankLogo } from "@/domains/bank/components/BankCard/components/SbankLogo";

function Root({
  children,
  className,
  isInteractive = true,
  onClick,
}: PropsWithChildren<{ className?: string; isInteractive?: boolean; onClick?: () => void }>) {
  const { cardRef, cardStyle, handleMouseMove, handleMouseEnter, handleMouseLeave } =
    usePerspectiveCardHover({
      maxRotation: 50,
      sensitivity: 0.5,
      enabled: isInteractive,
    });

  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onClick?.();
        }
      }}
      className={cn(
        "perspective-card-container relative shrink-0 w-[360px] h-[220px] rounded-[0.875rem] overflow-hidden shadow-md steam-card-glow steam-card-shadow",
        className,
      )}
      style={cardStyle}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

function Header({ children, className }: PropsWithChildren<{ className?: string }>) {
  return <div className={cn("flex items-center justify-between", className)}>{children}</div>;
}

function Body({ children, className }: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={cn("flex items-center justify-between px-7 mb-6", className)}>{children}</div>
  );
}

function Property({
  title,
  value,
  className,
}: PropsWithChildren<{ title: string; value: string; className?: string }>) {
  return (
    <div className={cn("flex flex-col gap-1 text-left", className)}>
      <div className="text-15 font-bold leading-none">{title}</div>
      <div className="text-13 text-[#ffffff99] leading-none">{value}</div>
    </div>
  );
}

function formatCardNumber(number: string | number): string {
  const numStr = number.toString().replace(/\D/g, "");
  const padded = numStr.padStart(16, "0");
  return padded.replace(/(\d{4})(?=\d)/g, "$1 ");
}

function Number({ value }: { value: string | number }) {
  const formattedValue = formatCardNumber(value);
  return <div className="text-18 text-[#ffffff] leading-none tabular-nums">{formattedValue}</div>;
}

function TierBadge({ tierName }: { tierName: string }) {
  return (
    <div className="h-9 px-7 flex items-center justify-center bg-gradient-to-r from-white/0 to-white/10">
      <span className="text-13 font-medium uppercase">{tierName}</span>
    </div>
  );
}

type BankCardTierInfo = {
  freeWithdrawalLimit: number;
  freeTransferLimit: number;
  monthlyFee: number;
};

function TierInfo({ freeWithdrawalLimit, freeTransferLimit, monthlyFee }: BankCardTierInfo) {
  return (
    <Body className="flex-col mt-auto items-start gap-6">
      <Property title={`До ${formatPrice(freeWithdrawalLimit)} ₽`} value="Снятие без комиссии" />

      <div className="flex justify-between w-full">
        <Property title={`До ${formatPrice(freeTransferLimit)} ₽`} value="Переводы без комиссии" />
        <Property
          title={`${formatPrice(monthlyFee)} ₽ / Мес.`}
          value="Обслуживание"
          className="items-end"
        />
      </div>
    </Body>
  );
}

function CardInfo({
  number,
  name,
  className,
}: PropsWithChildren<{ number: string; name: string; className?: string }>) {
  return (
    <Body className={cn("flex-col mt-auto h-[43.5%]", className)}>
      <div className="flex items-center justify-between w-full">
        <Number value={formatCardNumber(number)} />
        <PayPassIcon className="size-6" />
      </div>

      <div className="flex items-center justify-between w-full">
        <div className="text-12 uppercase max-w-[200px] truncate">{name}</div>
        <MigLogo />
      </div>
    </Body>
  );
}

function Template({
  tier = CardStyleAsset.Standard,
  name,
  children,
  isInteractive = false,
  onClick,
}: PropsWithChildren<{
  tier?: CardStyleAsset;
  name: string;
  isInteractive?: boolean;
  onClick?: () => void;
}>) {
  return (
    <Root
      className="flex flex-col font-tt-norms-pro"
      isInteractive={isInteractive}
      onClick={onClick}
    >
      <BankCardBg tier={tier} className="absolute inset-0 -z-1" />

      <Header className="pl-7 py-5">
        <SBankLogo />
        <TierBadge tierName={name} />
      </Header>

      {children}
    </Root>
  );
}

export const BankCard = {
  Root,
  Header,
  Body,
  Property,
  Number,
  TierBadge,
  TierInfo,
  CardInfo,
  Template,
};
